import React from 'react';
import {
  Activity,
  CheckCircle2,
  Droplets,
  FileText,
  MapPin,
  MessageSquare,
  ShoppingCart,
  ThermometerSun,
  Zap,
} from 'lucide-react';
import EvidenceButton from './EvidenceButton';
import { getEvidenceItemsForField } from '../../lib/evidenceUtils';
import {
  formatWatDateTime,
  getChecklistValue,
  getLatestReview,
  getReviewHistory,
  getStatusClasses,
} from '../../lib/visitUtils';

const scoreClass = (score) => {
  const value = String(score || '').toLowerCase();
  if (['great', 'strong', 'good', 'adequate'].some((item) => value.includes(item))) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (value.includes('fair')) return 'bg-amber-50 text-amber-800 border-amber-200';
  if (!score || score === '-') return 'bg-[#FAFAF7] text-[#5A5A5A] border-[#E8E6E3]';
  return 'bg-red-50 text-red-700 border-red-200';
};

const ScorePill = ({ children }) => (
  <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-medium ${scoreClass(children)}`}>{children || '-'}</span>
);

const Metric = ({ label, value, icon: Icon }) => (
  <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-4">
    <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">
      {Icon && <Icon className="h-4 w-4" />}
      {label}
    </p>
    <div className="mt-2 text-sm font-medium text-[#1A1A1A]">{value || '-'}</div>
  </div>
);

const GpsRow = ({ label, location, timestamp }) => (
  <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-4">
    <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">{label}</p>
    {location ? (
      <>
        <p className="mt-2 text-sm font-medium text-[#1A1A1A]">
          {location.lat ?? location.latitude}, {location.lng ?? location.longitude}
        </p>
        {location.accuracy ? <p className="mt-1 text-xs text-[#5A5A5A]">Accuracy: {Math.round(location.accuracy)}m</p> : null}
      </>
    ) : (
      <p className="mt-2 text-sm text-[#5A5A5A]">Not recorded</p>
    )}
    {timestamp ? <p className="mt-2 text-xs text-[#5A5A5A]">{formatWatDateTime(timestamp)}</p> : null}
  </div>
);

const VisitFullReport = ({ visit }) => {
  if (!visit) return null;

  const evidence = visit.evidence || {};
  const gps = visit.verification?.gps || {};
  const latestReview = getLatestReview(visit);
  const history = getReviewHistory(visit);
  const officerResponse = visit.latestOfficerResponse;
  const anomalies = visit.anomalies || [];
  const outcomeAlerts = visit.outcomeAlerts || [];
  const careExpenses = visit.careExpenses || [];

  return (
    <div className="space-y-5">
      <section className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
        <h3 className="mb-4 flex items-center gap-2 font-serif text-xl font-semibold text-[#1A1A1A]">
          <ThermometerSun className="h-5 w-5 text-[#9F1D35]" />
          Vitals
        </h3>
        <div className="grid gap-3 md:grid-cols-3">
          <Metric label="Temperature" value={`${getChecklistValue(visit, ['vitals', 'bodyTemperature', 'value'])} deg C`} />
          <Metric label="Blood Pressure" value={`${getChecklistValue(visit, ['vitals', 'bloodPressure', 'systolic'])}/${getChecklistValue(visit, ['vitals', 'bloodPressure', 'diastolic'])}`} />
          <Metric label="Appearance" value={<ScorePill>{getChecklistValue(visit, ['vitals', 'physicalAppearance', 'score'])}</ScorePill>} />
        </div>
      </section>

      <section className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
        <h3 className="mb-4 flex items-center gap-2 font-serif text-xl font-semibold text-[#1A1A1A]">
          <Activity className="h-5 w-5 text-[#9F1D35]" />
          Physical Activity
        </h3>
        <div className="grid gap-3 md:grid-cols-3">
          <Metric label="Ability to Stand" value={<ScorePill>{getChecklistValue(visit, ['physicalActivity', 'abilityToStand', 'score'])}</ScorePill>} />
          <Metric label="Ability to Walk" value={<ScorePill>{getChecklistValue(visit, ['physicalActivity', 'abilityToWalk', 'score'])}</ScorePill>} />
          <Metric label="Speech" value={<ScorePill>{getChecklistValue(visit, ['physicalActivity', 'speech', 'score'])}</ScorePill>} />
        </div>
      </section>

      <section className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
        <h3 className="mb-4 flex items-center gap-2 font-serif text-xl font-semibold text-[#1A1A1A]">
          <ShoppingCart className="h-5 w-5 text-[#9F1D35]" />
          Sustenance
        </h3>
        <div className="grid gap-3 md:grid-cols-3">
          <Metric label="Food" value={<ScorePill>{getChecklistValue(visit, ['sustenance', 'food', 'score'])}</ScorePill>} />
          <Metric label="Groceries" value={<ScorePill>{getChecklistValue(visit, ['sustenance', 'groceries', 'score'])}</ScorePill>} />
          <Metric label="Drinking Water" icon={Droplets} value={<ScorePill>{getChecklistValue(visit, ['sustenance', 'drinkingWater', 'score'])}</ScorePill>} />
        </div>
      </section>

      <section className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
        <h3 className="mb-4 flex items-center gap-2 font-serif text-xl font-semibold text-[#1A1A1A]">
          <Zap className="h-5 w-5 text-[#9F1D35]" />
          Utilities
        </h3>
        <div className="grid gap-3 md:grid-cols-2">
          <Metric label="Power Credit" value={<ScorePill>{getChecklistValue(visit, ['utilities', 'powerCredit', 'score'])}</ScorePill>} />
          <Metric label="Sanitation" value={<ScorePill>{getChecklistValue(visit, ['utilities', 'environmentalSanitation', 'score'])}</ScorePill>} />
        </div>
      </section>

      <section className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
        <h3 className="mb-4 flex items-center gap-2 font-serif text-xl font-semibold text-[#1A1A1A]">
          <FileText className="h-5 w-5 text-[#9F1D35]" />
          Evidence
        </h3>
        <div className="grid gap-3 md:grid-cols-2">
          <EvidenceButton label="Physical condition" reference={evidence.physicalCondition} items={getEvidenceItemsForField(visit, 'physicalCondition')} />
          <EvidenceButton label="Food" reference={evidence.food} items={getEvidenceItemsForField(visit, 'food')} />
          <EvidenceButton label="Groceries" reference={evidence.groceries} items={getEvidenceItemsForField(visit, 'groceries')} />
          <EvidenceButton label="Drinking water" reference={evidence.drinkingWater} items={getEvidenceItemsForField(visit, 'drinkingWater')} />
        </div>
      </section>

      <section className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
        <h3 className="mb-4 flex items-center gap-2 font-serif text-xl font-semibold text-[#1A1A1A]">
          <MapPin className="h-5 w-5 text-[#9F1D35]" />
          GPS Verification
        </h3>
        <div className="grid gap-3 md:grid-cols-2">
          <GpsRow label="Check-in" location={gps.checkInLocation} timestamp={gps.checkInTimestamp} />
          <GpsRow label="Check-out" location={gps.checkOutLocation} timestamp={gps.checkOutTimestamp} />
        </div>
        <div className="mt-3 rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-4 text-sm text-[#5A5A5A]">
          Distance from dependant: <span className="font-medium text-[#1A1A1A]">{gps.distanceFromDependant ?? 'Not calculated'}{gps.distanceFromDependant ? 'm' : ''}</span>
          <span className="mx-2">/</span>
          Location verified: <span className="font-medium text-[#1A1A1A]">{gps.locationVerified ? 'Yes' : 'No'}</span>
        </div>
        {visit.verification?.homePinStatus === 'missing' && (
          <p className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            Dependant home pin is not confirmed, so home-distance anomaly checks were skipped.
          </p>
        )}
      </section>

      {(anomalies.length > 0 || outcomeAlerts.length > 0 || careExpenses.length > 0) && (
        <section className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
          <h3 className="mb-4 flex items-center gap-2 font-serif text-xl font-semibold text-[#1A1A1A]">
            <CheckCircle2 className="h-5 w-5 text-[#9F1D35]" />
            Operational Outcomes
          </h3>
          <div className="grid gap-3 lg:grid-cols-3">
            <div className="rounded-2xl border border-[#E8E6E3] p-4">
              <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">Alerts</p>
              <div className="mt-3 space-y-2">
                {outcomeAlerts.map(alert => (
                  <div key={alert.id} className="rounded-xl bg-[#F7F4EE] p-3 text-sm">
                    <p className="font-medium text-[#1A1A1A]">{alert.type}</p>
                    <p className="mt-1 text-[#5A5A5A]">{alert.message}</p>
                    <p className="mt-1 text-xs text-[#5A5A5A]">{alert.severity} - {alert.releaseStatus || 'Released'}</p>
                  </div>
                ))}
                {outcomeAlerts.length === 0 && <p className="text-sm text-[#5A5A5A]">No alerts generated.</p>}
              </div>
            </div>

            <div className="rounded-2xl border border-[#E8E6E3] p-4">
              <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">Care Expenses</p>
              <div className="mt-3 space-y-2">
                {careExpenses.map(expense => (
                  <div key={expense.id} className="rounded-xl bg-[#F7F4EE] p-3 text-sm">
                    <p className="font-medium text-[#1A1A1A]">{expense.title}</p>
                    <p className="mt-1 text-[#5A5A5A]">{expense.explanation}</p>
                    <p className="mt-1 text-xs text-[#5A5A5A]">{expense.category} - {expense.status}</p>
                  </div>
                ))}
                {careExpenses.length === 0 && <p className="text-sm text-[#5A5A5A]">No care expenses submitted.</p>}
              </div>
            </div>

            <div className="rounded-2xl border border-[#E8E6E3] p-4">
              <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">Anomalies</p>
              <div className="mt-3 space-y-2">
                {anomalies.flatMap(item => item.anomalyFlags || []).map(flag => (
                  <div key={`${flag.ruleId}-${flag.createdAt}`} className="rounded-xl bg-amber-50 p-3 text-sm text-amber-950">
                    <p className="font-medium">{flag.name}</p>
                    <p className="mt-1">{flag.details}</p>
                    <p className="mt-1 text-xs">{flag.severity} - {flag.status}</p>
                  </div>
                ))}
                {anomalies.length === 0 && <p className="text-sm text-[#5A5A5A]">No anomalies detected.</p>}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
        <h3 className="mb-4 flex items-center gap-2 font-serif text-xl font-semibold text-[#1A1A1A]">
          <MessageSquare className="h-5 w-5 text-[#9F1D35]" />
          Observations
        </h3>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="rounded-2xl border border-[#E8E6E3] p-4">
            <p className="text-sm font-medium text-[#1A1A1A]">Other observations</p>
            <p className="mt-2 text-sm text-[#5A5A5A]">{getChecklistValue(visit, ['otherObservations'])}</p>
          </div>
          <div className="rounded-2xl border border-[#E8E6E3] p-4">
            <p className="text-sm font-medium text-[#1A1A1A]">General comments</p>
            <p className="mt-2 text-sm text-[#5A5A5A]">{getChecklistValue(visit, ['generalComments'])}</p>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
        <h3 className="mb-4 flex items-center gap-2 font-serif text-xl font-semibold text-[#1A1A1A]">
          <CheckCircle2 className="h-5 w-5 text-[#9F1D35]" />
          Review Thread
        </h3>
        <div className="space-y-3">
          {latestReview && (
            <div className="rounded-xl bg-[#F7F4EE] p-3">
              <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">Latest supervisor review</p>
              <p className={`mt-2 inline-flex rounded-full border px-3 py-1 text-xs font-medium ${getStatusClasses(latestReview.status || visit.status)}`}>{latestReview.status || visit.status}</p>
              {latestReview.comments && <p className="mt-2 text-sm text-[#5A5A5A]">{latestReview.comments}</p>}
            </div>
          )}
          {officerResponse && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-medium uppercase tracking-[0.12em] text-amber-800">Care officer response</p>
              <p className="mt-2 text-sm text-amber-900">{officerResponse.comments}</p>
              {officerResponse.respondedAt && <p className="mt-2 text-xs text-amber-800">{formatWatDateTime(officerResponse.respondedAt)}</p>}
            </div>
          )}
          {history.map((event) => (
            <div key={event.id || `${event.createdAt}-${event.type}`} className="rounded-xl border border-[#E8E6E3] p-3">
              <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">{event.type?.replaceAll('_', ' ') || 'Review event'}</p>
              <p className="mt-1 text-sm font-medium text-[#1A1A1A]">{event.status}</p>
              {event.comments && <p className="mt-2 text-sm text-[#5A5A5A]">{event.comments}</p>}
              {event.createdAt && <p className="mt-2 text-xs text-[#5A5A5A]">{formatWatDateTime(event.createdAt)}</p>}
            </div>
          ))}
          {!latestReview && !officerResponse && history.length === 0 && <p className="text-sm text-[#5A5A5A]">No review history yet.</p>}
        </div>
      </section>
    </div>
  );
};

export default VisitFullReport;
